import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from '../contexts/KeycloakAuthContext';
import { apiClient } from '../services/apiClient';
import type { Tenant } from './homeModel';

export interface Health {
  totals: { total: number; healthy: number; degraded: number; down: number; unknown: number };
  tenants: Array<{ slug: string; health_status?: string | null; last_checked_at?: string | null }>;
}
export interface Cost { configured: boolean; currency?: string; total: number | null; allocation_basis?: string }
interface Resource<T> { data: T | null; loading: boolean; error: Error | null }
const empty = <T,>(): Resource<T> => ({ data: null, loading: false, error: null });

function useResource<T>(path: string, enabled: boolean, identity: string, revision: number): Resource<T> {
  const [state, setState] = useState<Resource<T> & { key: string }>({ ...empty<T>(), key: '' });
  const key = `${identity}:${enabled}:${path}:${revision}`;
  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    setState({ data: null, loading: true, error: null, key });
    apiClient.get<T>(path, { signal: controller.signal }).then(({ data }) => {
      if (!controller.signal.aborted) setState({ data, loading: false, error: null, key });
    }).catch((error: unknown) => {
      if (!controller.signal.aborted) setState({ data: null, loading: false, error: error instanceof Error ? error : new Error('Could not load data'), key });
    });
    return () => controller.abort();
  }, [path, enabled, key]);
  // Permissions/identity changes hide the old data immediately, before effect cleanup.
  if (!enabled) return empty<T>();
  return state.key === key ? state : { data: null, loading: true, error: null };
}

export interface PilotDataValue {
  tenants: Tenant[];
  loading: boolean;
  error: Error | null;
  refresh: () => void;
  canReadTenants: boolean;
  canReadAnalytics: boolean;
  health: Health | null;
  healthLoading: boolean;
  healthError: Error | null;
  cost: Cost | null;
  costLoading: boolean;
  costError: Error | null;
}
const PilotDataContext = createContext<PilotDataValue | null>(null);
export function PilotDataProvider({ children }: { children: React.ReactNode }) {
  const { user, hasPermission } = useAuth();
  const canReadTenants = hasPermission('tenants.read') || hasPermission('tenants.manage');
  const canReadAnalytics = hasPermission('analytics.read');
  const identity = `${user?.id || user?.sub || user?.username || ''}:${JSON.stringify(user?.permissions || [])}`;
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision(n => n + 1), []);
  const tenants = useResource<{ tenants: Tenant[] }>('/api/provider/tenants', canReadTenants, identity, revision);
  const health = useResource<Health>('/api/provider/health', canReadTenants, identity, revision);
  const cost = useResource<Cost>('/api/provider/analytics/cost', canReadAnalytics, identity, revision);
  const tenantRows = (tenants.data?.tenants || []).map(tenant => {
    const latest = health.data?.tenants.find(row => row.slug === tenant.slug);
    return latest ? { ...tenant, health_status: latest.health_status, health_last_checked_at: latest.last_checked_at } : tenant;
  });
  return <PilotDataContext.Provider value={{ tenants: tenantRows, loading: tenants.loading, error: tenants.error, refresh, canReadTenants, canReadAnalytics, health: health.data, healthLoading: health.loading, healthError: health.error, cost: cost.data, costLoading: cost.loading, costError: cost.error }}>{children}</PilotDataContext.Provider>;
}
export function usePilotData(): PilotDataValue {
  const value = useContext(PilotDataContext);
  if (!value) throw new Error('usePilotData requires PilotDataProvider');
  return value;
}
